import { useCallback, useEffect, useRef } from 'react'

interface VoiceChunk {
  data: string // base64 encoded audio
  timestamp: number
}

export const useVoicePlayer = (isEnabled: boolean, play: boolean) => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const voiceQueueRef = useRef<VoiceChunk[]>([])
  const isPlayingRef = useRef(false)
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const playEnabledRef = useRef(play)
  const sessionIdRef = useRef<number>(0) // Track current playback session

  // Update play state ref
  useEffect(() => {
    playEnabledRef.current = play
  }, [play])

  // Initialize AudioContext
  useEffect(() => {
    if (isEnabled && !audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [isEnabled])

  // Convert base64 to ArrayBuffer
  const base64ToArrayBuffer = useCallback((base64: string): ArrayBuffer => {
    const binaryString = window.atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }, [])

  // Play next chunk from queue
  const playNextChunk = useCallback(async () => {
    if (!audioContextRef.current || !playEnabledRef.current || isPlayingRef.current) {
      return
    }

    const chunk = voiceQueueRef.current.shift()
    if (!chunk) {
      isPlayingRef.current = false
      return
    }

    const currentSession = sessionIdRef.current

    try {
      isPlayingRef.current = true

      // Resume AudioContext if it's suspended (browser policy)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume()
      }

      const arrayBuffer = base64ToArrayBuffer(chunk.data)
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer)

      // Check if we're still in the same session and play is still enabled
      if (currentSession !== sessionIdRef.current || !playEnabledRef.current) {
        isPlayingRef.current = false
        return
      }

      const source = audioContextRef.current.createBufferSource()
      source.buffer = audioBuffer
      source.connect(audioContextRef.current.destination)
      currentSourceRef.current = source

      source.onended = () => {
        currentSourceRef.current = null
        isPlayingRef.current = false
        // Check if we're still in the same session before playing next
        if (currentSession === sessionIdRef.current && playEnabledRef.current) {
          playNextChunk()
        }
      }

      source.start(0)
    } catch (error) {
      console.error('Error playing voice chunk:', error)
      isPlayingRef.current = false
      // Try to play next chunk even if this one failed
      if (currentSession === sessionIdRef.current && playEnabledRef.current) {
        playNextChunk()
      }
    }
  }, [base64ToArrayBuffer])

  // Add voice chunk to queue
  const addVoiceChunk = useCallback(
    (voiceData: string) => {
      if (!isEnabled) return

      const chunk: VoiceChunk = {
        data: voiceData,
        timestamp: Date.now(),
      }

      voiceQueueRef.current.push(chunk)

      // If play is enabled and not currently playing, start playback
      if (playEnabledRef.current && !isPlayingRef.current) {
        playNextChunk()
      }
    },
    [isEnabled, playNextChunk]
  )

  // Stop current playback
  const stopPlayback = useCallback(() => {
    if (currentSourceRef.current) {
      try {
        currentSourceRef.current.stop()
        currentSourceRef.current.disconnect()
      } catch (error) {
        // Ignore errors if already stopped
      }
      currentSourceRef.current = null
    }
    isPlayingRef.current = false
  }, [])

  // Clear queue and stop playback (when user sends new message or closes modal)
  const resetPlayback = useCallback(() => {
    stopPlayback()
    voiceQueueRef.current = []
    sessionIdRef.current += 1 // Increment session to invalidate any pending playback
    isPlayingRef.current = false
  }, [stopPlayback])

  // Handle play toggle
  useEffect(() => {
    if (play) {
      // User enabled play - start playing if there are chunks in queue
      if (voiceQueueRef.current.length > 0 && !isPlayingRef.current) {
        sessionIdRef.current += 1 // New session when re-enabling
        playNextChunk()
      }
    } else {
      // User disabled play - stop current playback
      stopPlayback()
    }
  }, [play, playNextChunk, stopPlayback])

  return {
    addVoiceChunk,
    resetPlayback,
    stopPlayback,
  }
}
